import { Badge, DataTable, ErrorState, PageHeader, type ColumnDef } from '@zuruflex/ui';
import { formatDateTimeIn, LOCALES, toLocale } from '@zuruflex/i18n';
import type { Customer, Paginated } from '@zuruflex/types';
import { formatPhone } from '@zuruflex/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppVersion } from '../components/AppVersion.js';
import { api } from '../lib/api.js';
import { useTableLabels } from '../lib/table-labels.js';

const KYC_VARIANT = {
  VERIFIED: 'success',
  PENDING: 'warning',
  REJECTED: 'danger',
  UNVERIFIED: 'default',
} as const;

export function CustomersPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.request<Paginated<Customer>>('GET', '/v1/customers?perPage=100'),
  });

  const columns = useMemo<ColumnDef<Customer>[]>(
    () => [
      {
        accessorKey: 'fullName',
        header: t('common:field.name'),
        cell: ({ row }) =>
          row.original.fullName ?? (
            <span className="text-text-muted">{t('customers.unnamed')}</span>
          ),
      },
      {
        accessorKey: 'phone',
        header: t('common:field.phone'),
        // Blank for an account made on the website, which has an email and
        // no number until the first hire asks for one.
        cell: ({ row }) =>
          row.original.phone ? (
            <span className="font-mono">{formatPhone(row.original.phone)}</span>
          ) : (
            <span className="text-text-muted">—</span>
          ),
      },
      {
        accessorKey: 'locale',
        header: t('common:field.language'),
        // The customer's own language, not the operator's — it drives every
        // message the bot sends them.
        cell: ({ row }) => (
          <Badge variant="outline">{LOCALES[toLocale(row.original.locale)].nativeName}</Badge>
        ),
      },
      {
        accessorKey: 'channel',
        header: t('common:field.channel'),
        cell: ({ row }) => <Badge>{row.original.channel}</Badge>,
      },
      {
        accessorKey: 'kycStatus',
        header: 'KYC',
        cell: ({ row }) => (
          <Badge variant={KYC_VARIANT[row.original.kycStatus]}>
            {t(`common:kycStatus.${row.original.kycStatus}`)}
          </Badge>
        ),
      },
      {
        // What the concierge remembers: their last trip and the number they pay with.
        id: 'lastTrip',
        header: t('customers.lastTrip'),
        cell: ({ row }) => {
          const customer = row.original;
          if (!customer.lastVehicleClass && !customer.momoNumber) {
            return <span className="text-text-muted">—</span>;
          }
          return (
            <div className="text-sm">
              {customer.lastVehicleClass ? (
                <div>
                  {t(`common:vehicleClass.${customer.lastVehicleClass}`)}
                  {customer.lastWithDriver ? ` · ${t('customers.withDriver')}` : ''}
                  {customer.lastDeliveryAddress
                    ? ` · ${t('customers.deliveredTo', { where: customer.lastDeliveryAddress })}`
                    : ''}
                </div>
              ) : null}
              {customer.momoNumber ? (
                <div className="font-mono text-xs text-text-muted">
                  {t('customers.momo', { number: formatPhone(customer.momoNumber) })}
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'app',
        header: t('app.column'),
        cell: ({ row }) => <AppVersion app={row.original.app} />,
      },
      {
        accessorKey: 'createdAt',
        header: t('common:field.joined'),
        cell: ({ row }) => formatDateTimeIn(row.original.createdAt, locale),
      },
    ],
    [t, locale],
  );

  return (
    <>
      <PageHeader
        title={t('customers.title')}
        description={data ? t('customers.count', { count: data.total }) : t('bookings.loading')}
      />

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          labels={tableLabels}
          columns={columns}
          data={data?.items ?? []}
          isLoading={isPending}
          searchColumn="fullName"
          searchPlaceholder={t('customers.searchPlaceholder')}
          emptyMessage={t('customers.empty')}
        />
      )}
    </>
  );
}
